import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCategory,
  deleteCategory,
  listCategories,
  updateCategory,
  type CreateCategoryRequest,
  type UpdateCategoryRequest,
} from "../api/client";
import { ApiError, type Category } from "../api/types";
import { queryKeys } from "./queryKeys";

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: listCategories,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation<Category, ApiError, CreateCategoryRequest>({
    mutationFn: (payload) => createCategory(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
    },
  });
}

interface UpdateCategoryVars {
  id: string;
  payload: UpdateCategoryRequest;
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation<Category, ApiError, UpdateCategoryVars>({
    mutationFn: ({ id, payload }) => updateCategory(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      // Deleting a category un-sets it on its books, not delete them — refresh the library too.
      void queryClient.invalidateQueries({ queryKey: ["books"] });
    },
  });
}
